import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getClerkAuthHeaders, getDemoAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";

export interface InboxLink {
  id: string;
  projectId: string;
  token: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string | null;
}

export interface InboxLinkCustomization {
  id: string;
  inboxLinkId: string;
  shortCode: string;
  prefillName: string | null;
  gcNote: string | null;
  label: string | null;
  createdAt: string;
  createdBy: string | null;
  url?: string;
}

export interface InboxSubmission {
  id: string;
  projectId: string;
  submitterName: string;
  submitterPhone: string | null;
  submitterEmail: string | null;
  photoUrl: string | null;
  message: string;
  category: "checkpoint" | "issue" | "delivery" | "receipt" | "general";
  referenceNumber: string;
  status: "new" | "acknowledged" | "replied" | "archived" | "linked";
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  replyMessage: string | null;
  repliedAt: string | null;
  dailyLogId: string | null;
  linkedAt: string | null;
  linkedBy: string | null;
  customizationId: string | null;
  customization?: InboxLinkCustomization | null;
  createdAt: string;
}

interface InboxLinkResponse {
  link: InboxLink | null;
  url?: string;
  message?: string;
}

interface InboxSubmissionsResponse {
  submissions: InboxSubmission[];
  newCount: number;
  total: number;
}

interface SubmissionResponse {
  submission: InboxSubmission;
}

/**
 * Get inbox link for a project
 */
export function useInboxLink(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "inbox", "link"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<InboxLinkResponse>(
        `/api/projects/${projectId}/inbox/link`,
        { headers }
      );
    },
    enabled: auth.isSignedIn && !!projectId,
  });
}

/**
 * Create inbox link for a project
 */
export function useCreateInboxLink(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<InboxLinkResponse>(
        `/api/projects/${projectId}/inbox/link`,
        {
          method: "POST",
          headers,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "inbox", "link"] });
    },
  });
}

/**
 * Update inbox link (toggle active, regenerate token)
 */
export function useUpdateInboxLink(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { isActive?: boolean; regenerateToken?: boolean }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<InboxLinkResponse>(
        `/api/projects/${projectId}/inbox/link`,
        {
          method: "PATCH",
          headers,
          body: JSON.stringify(data),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "inbox", "link"] });
    },
  });
}

/**
 * Get inbox submissions for a project
 */
export function useInboxSubmissions(projectId: string, status?: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "inbox", "submissions", status],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const url = status
        ? `/api/projects/${projectId}/inbox?status=${status}`
        : `/api/projects/${projectId}/inbox`;

      return fetchApi<InboxSubmissionsResponse>(url, { headers });
    },
    enabled: auth.isSignedIn && !!projectId,
  });
}

/**
 * Acknowledge a submission
 */
export function useAcknowledgeSubmission(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (submissionId: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<SubmissionResponse>(
        `/api/projects/${projectId}/inbox/${submissionId}/acknowledge`,
        {
          method: "POST",
          headers,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "inbox"] });
    },
  });
}

/**
 * Reply to a submission
 */
export function useReplyToSubmission(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ submissionId, message }: { submissionId: string; message: string }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<SubmissionResponse>(
        `/api/projects/${projectId}/inbox/${submissionId}/reply`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ message }),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "inbox"] });
    },
  });
}

/**
 * Archive a submission
 */
export function useArchiveSubmission(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (submissionId: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<SubmissionResponse>(
        `/api/projects/${projectId}/inbox/${submissionId}/archive`,
        {
          method: "POST",
          headers,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "inbox"] });
    },
  });
}

/**
 * Link a submission to a daily log
 */
export function useLinkSubmissionToLog(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      submissionId,
      dailyLogId,
    }: {
      submissionId: string;
      dailyLogId: string;
    }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<SubmissionResponse>(
        `/api/projects/${projectId}/inbox/${submissionId}/link-to-log`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ dailyLogId }),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "inbox"] });
    },
  });
}

// ============================================
// LINK CUSTOMIZATIONS
// ============================================

interface CustomizationsResponse {
  customizations: InboxLinkCustomization[];
}

interface CreateCustomizationResponse {
  customization: InboxLinkCustomization;
  url: string;
}

/**
 * Get all customizations for a project's inbox link
 */
export function useInboxCustomizations(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "inbox", "customizations"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CustomizationsResponse>(
        `/api/projects/${projectId}/inbox/customizations`,
        { headers }
      );
    },
    enabled: auth.isSignedIn && !!projectId,
  });
}

/**
 * Create a customized inbox link
 */
export function useCreateCustomization(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { prefillName?: string; gcNote?: string; label?: string }) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<CreateCustomizationResponse>(
        `/api/projects/${projectId}/inbox/customizations`,
        {
          method: "POST",
          headers,
          body: JSON.stringify(data),
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "inbox", "customizations"] });
    },
  });
}

/**
 * Delete a customization
 */
export function useDeleteCustomization(projectId: string) {
  const auth = useAuthConditional();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (customizationId: string) => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      return fetchApi<{ success: boolean }>(
        `/api/projects/${projectId}/inbox/customizations/${customizationId}`,
        {
          method: "DELETE",
          headers,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects", projectId, "inbox", "customizations"] });
    },
  });
}
